import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';
import { Avatar, Button, Card, CardContent, CardHeader, Container, Grid, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useLoadingContext } from '../../context/loadingContext';

import Label from '../../components/Label';
import Page from '../../components/Page';
import { RecruitService } from '../../Services/recruit.service';

const ViewRecruit = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const lang = i18n.language;
  const [recruit, setRecuit] = useState({});
  const [logs, setLogs] = useState([]);
  const { handleStartLoading, handleStopLoading } = useLoadingContext()

  useEffect(() => {
    getRecruitBySlug().finally(handleStopLoading);
  }, []);

  const getRecruitBySlug = async () => {
    handleStartLoading()
    const { promotion: promotionData, logs } = await RecruitService.GetRecruitBySlug(slug);
    setRecuit(promotionData);
    setLogs(logs);
  };

  const handleDisableClick = () => {
    handleStartLoading()
    RecruitService.DisableRecruit(recruit._id)
      .then(() => {
        setRecuit((prevState) => ({
          ...prevState,
          status: 'Inactive',
        }));
        enqueueSnackbar('Thành công', { variant: 'success' });
      })
      .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
      .finally(handleStopLoading)
  };

  const handleEnableClick = () => {
    handleStartLoading()
    RecruitService.EnableRecruit(recruit._id)
      .then(() => {
        setRecuit((prevState) => ({
          ...prevState,
          status: 'Active',
        }));
        enqueueSnackbar('Thành công', { variant: 'success' });
      })
      .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
      .finally(handleStopLoading)
  };

  const handleDeleteClick = () => {
    Swal.fire({
      customClass: {
        container: { zIndex: 999999 },
      },
      title: 'Xóa bài viết',
      text: 'Thao tác này không thể hoàn tác',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: t('sure.yes'),
      cancelButtonText: t('sure.no'),
    }).then((result) => {
      if (result.isConfirmed) {
        handleStartLoading()
        RecruitService.DeleteRecruit(recruit._id)
          .then(() => {
            navigate('/recruit');
            enqueueSnackbar('Thành công', { variant: 'success' });
          })
          .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
          .finally(handleStopLoading)
      }
    });
  };

  return (
    <Container maxWidth="xl">
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Page title="page">
            <Card>
              <CardContent>
                <Typography textAlign="center" variant="h4">
                  {recruit && recruit.title}
                </Typography>
                <Typography textAlign="center" mt={2} variant="subtitle1">
                  {recruit && recruit.subTitle}
                </Typography>
                <div className="ck-content" dangerouslySetInnerHTML={{ __html: recruit && recruit.content }} />
              </CardContent>
            </Card>
          </Page>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Card>
              <CardHeader title="Chi tiết" />
              <CardContent>
                {recruit?.author?.avatar.match('https://') ? (
                  <Avatar
                    sx={{
                      width: '50px',
                      height: '50px',
                    }}
                    src={recruit?.author?.avatar}
                    alt="photoURL"
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: '50px',
                      height: '50px',
                    }}
                    src={`${process.env.REACT_APP_IMAGE}${recruit?.author?.avatar}`}
                    alt="photoURL"
                  />
                )}

                <Typography fontWeight={700}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                    }}
                  >
                    Username:{' '}
                  </span>
                  {recruit && recruit.author?.username}
                </Typography>
                <Typography fontWeight={700}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                    }}
                  >
                    Họ và tên:{' '}
                  </span>
                  {recruit && recruit.author?.fullname}
                </Typography>
                <Typography fontWeight={700}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                    }}
                  >
                    Lương:
                  </span>
                  {recruit && Number(recruit.price) === 0 ? 'Thỏa thuận' : recruit.price}
                </Typography>
                <Typography fontWeight={700}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                    }}
                  >
                    Loại lao động:
                  </span>
                  {recruit && recruit.recruitType}
                </Typography>
                <Typography fontWeight={700}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                    }}
                  >
                    Trạng thái:{' '}
                  </span>
                  {recruit && (
                    <Label variant="ghost" color={(recruit?.status === 'Inactive' && 'error') || 'success'}>
                      {recruit.status}
                    </Label>
                  )}
                  {recruit && recruit.isHotPromotion && (
                    <Label variant="ghost" color={'success'} sx={{ ml: 1 }}>
                      Khuyễn mại hot
                    </Label>
                  )}
                  {recruit && recruit.showBeforePublic && (
                    <Label variant="ghost" color={'success'} sx={{ ml: 1 }}>
                      Luôn hiển thị
                    </Label>
                  )}
                </Typography>
                <Stack spacing={1} mt={1}>
                  <Button
                    onClick={() => navigate(`/recruit/edit/${recruit.slug}`)}
                    variant="outlined"
                    fullWidth
                    color="inherit"
                  >
                    Cập nhật
                  </Button>
                  <Button
                    onClick={handleDisableClick}
                    disabled={recruit?.status === 'Inactive'}
                    variant="outlined"
                    fullWidth
                    color="warning"
                  >
                    Ẩn bài viết
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleEnableClick}
                    disabled={recruit?.status === 'Active'}
                  >
                    Hiện bài viết
                  </Button>
                  <Button onClick={handleDeleteClick} fullWidth color="error" variant="outlined">
                    Xóa bài viết
                  </Button>
                </Stack>
              </CardContent>
            </Card>
            <Card>
              <CardHeader title="Logs" />
              <CardContent
                sx={{
                  '& .MuiTimelineItem-missingOppositeContent:before': {
                    display: 'none',
                  },
                }}
              >
                <Timeline>
                  {logs &&
                    logs.map((item, index) => (
                      <OrderItem
                        key={item._id}
                        item={{
                          time: item.time,
                          title: item[lang],
                          type: item.logType,
                          username: item.user.username,
                        }}
                        isLast={index === logs.length - 1}
                      />
                    ))}
                </Timeline>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

// ----------------------------------------------------------------------

OrderItem.propTypes = {
  isLast: PropTypes.bool,
  item: PropTypes.shape({
    time: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    username: PropTypes.string,
  }),
};

function OrderItem({ item, isLast }) {
  const { type, title, time } = item;
  return (
    <TimelineItem>
      <TimelineSeparator>
        <TimelineDot
          color={
            (type === 'enable' && 'primary') ||
            (type === 'create' && 'success') ||
            (type === 'reset' && 'info') ||
            (type === 'edit' && 'warning') ||
            (type === 'disable' && 'grey') ||
            'error'
          }
        />
        {isLast ? null : <TimelineConnector />}
      </TimelineSeparator>

      <TimelineContent>
        <Typography variant="subtitle1">{title}</Typography>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
          @{item.username}
        </Typography>

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {moment(time).format('DD/MM/YYYY-HH:mm')}
        </Typography>
      </TimelineContent>
    </TimelineItem>
  );
}

export default ViewRecruit;
